// Quotes tile (companion) — READ-ONLY consult: list · search · open the branded proposal portal.
// Composing quotes and Solutions stays desktop-only (Outlook pane · desktop PWA). Reads are
// same-origin (/api/quotes GET, rscan_sid cookie) off the SAME shared Neon; the endpoint is
// org-gated (quotes_enabled) — a 403 simply hides the tile. Row chassis = the shared .rc-row.
import { toast } from "./app";
import { loadSnap, saveSnap, skeletonHtml } from "./cache";

const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);

type Quote = {
  id: number | string; // Neon returns BIGINT as a string
  ref: string | null;
  customer_company: string | null;
  status: string; // draft | published
  stage: string | null;
  total_excl_cents: number | string | null;
  valid_until: string | null;
  slug: string | null;
  accepted_at: string | null;
  revoked_at: string | null;
  sent_at: string | null;
};

let all: Quote[] = [];
let loaded = false;
let statusFilter = "all"; // All · Draft · Live · Sent · Signed · Offline (matches statusLabel())

const esc = (s: unknown): string =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));

// € from integer cents, EU format (matches the desktop euros()).
const euros = (cents: number | string | null): string => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return "—";
  return "€ " + (n / 100).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// System status label (mirrors the desktop qStatus — the pipeline STAGE is a separate chip).
function statusLabel(q: Quote): string {
  if (q.status === "draft") return "Draft";
  if (q.revoked_at) return "Offline";
  if (q.accepted_at) return "Signed";
  return q.sent_at ? "Sent" : "Live";
}

// Quotes is an org feature. The tile is ALWAYS shown so it stays discoverable; when the org hasn't
// enabled it (403) it goes greyed (locked) with a small lock — clicking still opens the screen,
// which explains how to switch it on.
let enabled = true;
function gateTile(on: boolean): void {
  enabled = on;
  const tile = document.querySelector<HTMLElement>('[data-tile="quotes"]');
  if (!tile) return;
  tile.classList.toggle("locked", !on);
  const ico = tile.querySelector(".tt-ic");
  const lock = tile.querySelector<HTMLElement>(".tt-lock");
  if (!on && ico && !lock) {
    const s = document.createElement("span");
    s.className = "tt-lock";
    s.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';
    ico.appendChild(s);
  } else if (on && lock) {
    lock.remove();
  }
}

// Stale-while-revalidate: paint the last-known list (or a skeleton on a true first run)
// immediately, then fetch and re-render. The screen is never blanked while loading.
export async function loadQuotes(force = false): Promise<void> {
  if (loaded && !force) { renderList(); return; }
  let painted = loaded;
  if (!loaded) {
    const snap = loadSnap<Quote[]>("quotes");
    if (snap) { all = snap; painted = true; gateTile(true); renderList(); }
    else { const list = $("#quotesList"); if (list) list.innerHTML = skeletonHtml(); }
  }
  try {
    const r = await fetch("/api/quotes", { credentials: "include" });
    if (r.status === 401) { renderEmpty("This phone isn't linked. Reconnect from Outlook to load your quotes."); return; }
    if (r.status === 403) { gateTile(false); all = []; loaded = true; saveSnap("quotes", all); renderList(); return; } // quotes not enabled for this org
    if (!r.ok) throw new Error(String(r.status));
    const j = (await r.json()) as { quotes?: Quote[] };
    all = j.quotes || [];
    loaded = true;
    gateTile(true);
    saveSnap("quotes", all);
    renderList();
  } catch {
    if (!painted) renderEmpty("Couldn't load your quotes. Check your connection and try again.");
  }
}

function renderEmpty(msg: string): void {
  const list = $("#quotesList"); const empty = $("#quotesEmpty");
  if (list) list.innerHTML = "";
  if (empty) { empty.textContent = msg; empty.style.display = "block"; }
  const count = $("#topCount"); if (count) { count.textContent = ""; count.hidden = true; }
}

// ── list (flat, searchable) ──
function renderList(): void {
  const term = ($("#quotesSearch") as HTMLInputElement | null)?.value.trim().toLowerCase() || "";
  const items = all.filter((q) =>
    (statusFilter === "all" || statusLabel(q) === statusFilter) &&
    (!term || `${q.ref || ""} ${q.customer_company || ""} ${q.stage || ""}`.toLowerCase().includes(term)));

  // Active-screen gate: the Home-boot prefetch renders in the background.
  const count = $("#topCount");
  if (count && $("#screen-quotes")?.classList.contains("active")) { count.textContent = all.length ? String(all.length) : ""; count.hidden = !all.length; }

  const empty = $("#quotesEmpty"); const list = $("#quotesList");
  if (!list) return;
  if (!items.length) {
    list.innerHTML = "";
    if (empty) {
      empty.textContent = !enabled
        ? "Quotes isn’t enabled for your organization yet. Ask your admin to switch it on."
        : all.length ? "No quote matches your search." : "No quotes yet — compose them on the desktop app, then open their portal here.";
      empty.style.display = "block";
    }
    return;
  }
  if (empty) empty.style.display = "none";

  const tag = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L3 13V3h10l7.59 7.59a2 2 0 0 1 0 2.82z"/><circle cx="7.5" cy="7.5" r="1.3" fill="currentColor"/></svg>`;
  list.innerHTML = items
    .map((q) => {
      const sub = [statusLabel(q), q.stage, q.valid_until ? "until " + String(q.valid_until).slice(0, 10) : ""].filter(Boolean).join(" · ");
      return `<button class="rc-row" data-id="${esc(q.id)}">` +
        `<span class="rc-rowic">${tag}</span>` +
        `<span class="rc-rowbody"><span class="rc-rowtitle">${esc(q.customer_company || q.ref || "—")}</span><span class="rc-rowsub">${esc(sub)}</span></span>` +
        `<span class="rc-rowamt">${esc(euros(q.total_excl_cents))}</span>` +
        `</button>`;
    })
    .join("");
}

// Open the branded proposal. Published → the live portal. A draft/unpublished quote has no public
// portal (and building/previewing drafts is desktop-only), so we point the user back to the desktop.
function openQuote(q: Quote): void {
  if (q.status === "published" && q.slug && !q.revoked_at) {
    window.open(`https://proposals.rplay.be/${q.slug}`, "_blank", "noopener"); // inside the click gesture (Safari)
  } else {
    toast(q.revoked_at ? "This proposal is offline." : "This quote isn't published yet — open it on the desktop app.");
  }
}

/** Home-search result tap → open the quote's portal (after loadQuotes' snapshot paint). */
export function openQuoteById(id: string): void {
  const q = all.find((x) => String(x.id) === id);
  if (q) openQuote(q);
}

// ── wiring ──
export function initQuotes(): void {
  const search = $("#quotesSearch") as HTMLInputElement | null;
  const onSearch = (): void => { renderList(); const cl = $("#quotesClear"); if (cl) cl.hidden = !search?.value; };
  search?.addEventListener("input", onSearch);
  $("#quotesClear")?.addEventListener("click", () => { if (search) search.value = ""; onSearch(); search?.focus(); });
  // status filter chips (All · Draft · Live · Sent · Signed · Offline)
  $("#quotesChips")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-st]");
    if (!b) return;
    statusFilter = b.getAttribute("data-st") || "all";
    document.querySelectorAll<HTMLElement>("#quotesChips [data-st]").forEach((x) => x.classList.toggle("on", x === b));
    renderList();
  });
  $("#quotesList")?.addEventListener("click", (e) => {
    const row = (e.target as HTMLElement).closest<HTMLElement>(".rc-row");
    if (row) { const q = all.find((x) => String(x.id) === (row.getAttribute("data-id") || "")); if (q) openQuote(q); }
  });
}
